import PropTypes from 'prop-types';
import { useState, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
// @mui
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Tooltip from '@mui/material/Tooltip';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TableContainer from '@mui/material/TableContainer';
import LinearProgress from '@mui/material/LinearProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TablePagination from '@mui/material/TablePagination';
// utils
import { fDateTime, fToNow } from 'src/utils/format-time';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';

// ----------------------------------------------------------------------

export default function NotificationDetailDialog({
  open,
  onClose,
  notification,
  onNavigate,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRow, setExpandedRow] = useState(null);

  const rawErrors = useMemo(() => {
    const t = notification?.background_task;
    const m = notification?.meta_data || t?.result_summary || {};
    return m?.errors || t?.result_summary?.errors || [];
  }, [notification]);

  // Normalize error objects into consistent schema
  const normalizedErrors = useMemo(() => {
    if (!Array.isArray(rawErrors)) return [];
    return rawErrors.map((item, idx) => {
      if (typeof item === 'string') {
        return {
          id: idx + 1,
          name: 'N/A',
          className: '-',
          phone: '-',
          parentName: '-',
          gender: '-',
          dob: '-',
          city: '-',
          address: '-',
          desiredStream: '-',
          error: item,
          raw: {},
        };
      }
      if (item && typeof item === 'object') {
        const row = item.row || {};
        const errorText =
          item.error ||
          item.message ||
          (typeof Object.values(item)[0] === 'string'
            ? Object.values(item)[0]
            : JSON.stringify(item));

        return {
          id: idx + 1,
          name: row.Name || row.name || row.full_name || 'N/A',
          className: row.Class || row.class || row.attending_class || '-',
          phone: row.Phone || row.phone || row.phone_number || '-',
          parentName:
            row['Father name'] ||
            row.father_name ||
            row['Mother name'] ||
            row.mother_name ||
            '-',
          gender: row.Gender || row.gender || '-',
          dob: row.dob || row.date_of_birth || '-',
          city: row.City || row.city || '-',
          state: row.State || row.state || '-',
          address: row.Address || row.address || '-',
          pincode: row.Pincode || row.pincode || '-',
          desiredStream:
            row['Desired Stream'] || row.desired_stream || row.stream || '-',
          error: errorText,
          raw: row,
        };
      }
      return {
        id: idx + 1,
        name: 'N/A',
        className: '-',
        phone: '-',
        parentName: '-',
        gender: '-',
        dob: '-',
        city: '-',
        address: '-',
        desiredStream: '-',
        error: String(item),
        raw: {},
      };
    });
  }, [rawErrors]);

  // Filtered error records based on search
  const filteredErrors = useMemo(() => {
    if (!searchQuery.trim()) return normalizedErrors;
    const q = searchQuery.toLowerCase();
    return normalizedErrors.filter(
      (err) =>
        err.name.toLowerCase().includes(q) ||
        err.phone.toLowerCase().includes(q) ||
        err.className.toLowerCase().includes(q) ||
        err.parentName.toLowerCase().includes(q) ||
        err.error.toLowerCase().includes(q)
    );
  }, [normalizedErrors, searchQuery]);

  if (!notification) return null;

  const task = notification.background_task;
  const meta = notification.meta_data || task?.result_summary || {};

  const createdCount =
    meta.created_count ??
    meta.success_count ??
    task?.result_summary?.created_count ??
    task?.result_summary?.success_count ??
    null;

  const errorCount =
    meta.error_count ??
    meta.failure_count ??
    task?.result_summary?.error_count ??
    task?.result_summary?.failure_count ??
    rawErrors.length;

  const schoolName = meta.school_name || task?.result_summary?.school_name || '';
  const centerName = meta.center_name || task?.result_summary?.center_name || '';

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportErrorsXlsx = () => {
    if (!normalizedErrors.length) return;

    const exportRows = normalizedErrors.map((item) => ({
      'Row #': item.id,
      'Student Name': item.name,
      'Class': item.className,
      'Phone': item.phone,
      'Parent Name': item.parentName,
      'Gender': item.gender,
      'Date of Birth': item.dob,
      'City': item.city,
      'State': item.state || '',
      'Address': item.address,
      'Pincode': item.pincode,
      'Desired Stream': item.desiredStream,
      'Reason for Failure / Error': item.error,
    }));

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Failed Records');

    const cleanSchool = (schoolName || 'Bulk_Import').replace(/[^a-zA-Z0-9_-]/g, '_');
    const dateStr = new Date().toISOString().slice(0, 10);
    const filename = `${cleanSchool}_Error_Records_${dateStr}.xlsx`;

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), filename);
  };

  const getStatusIcon = () => {
    switch (notification.notification_type) {
      case 'SUCCESS':
      case 'TASK_COMPLETED':
        return {
          icon: 'eva:checkmark-circle-2-fill',
          color: 'success.main',
          bgcolor: 'success.lighter',
        };
      case 'ERROR':
      case 'TASK_FAILED':
        return {
          icon: 'eva:alert-circle-fill',
          color: 'error.main',
          bgcolor: 'error.lighter',
        };
      case 'WARNING':
        return {
          icon: 'eva:alert-triangle-fill',
          color: 'warning.main',
          bgcolor: 'warning.lighter',
        };
      default:
        return {
          icon: 'solar:info-circle-bold',
          color: 'primary.main',
          bgcolor: 'primary.lighter',
        };
    }
  };

  const statusIcon = getStatusIcon();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ p: 2.5, pb: 1.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: statusIcon.bgcolor,
                color: statusIcon.color,
                flexShrink: 0,
              }}
            >
              <Iconify icon={statusIcon.icon} width={26} />
            </Box>

            <Box>
              <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
                {notification.title}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {fDateTime(notification.created_at)} ({fToNow(notification.created_at)})
                </Typography>
                {notification.module && notification.module !== 'general' && (
                  <Label variant="soft" color="info" sx={{ height: 20, fontSize: 10 }}>
                    {notification.module.replace(/_/g, ' ').toUpperCase()}
                  </Label>
                )}
                {notification.notification_type && (
                  <Label
                    variant="soft"
                    color={
                      notification.notification_type.includes('COMPLETED') ||
                      notification.notification_type === 'SUCCESS'
                        ? 'success'
                        : notification.notification_type.includes('FAIL')
                        ? 'error'
                        : 'default'
                    }
                    sx={{ height: 20, fontSize: 10 }}
                  >
                    {notification.notification_type}
                  </Label>
                )}
              </Stack>
            </Box>
          </Stack>

          <IconButton onClick={onClose} size="small">
            <Iconify icon="mingcute:close-line" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: 1 }}>
        <Stack spacing={2.5}>
          {/* Main message alert */}
          {notification.message && (
            <Alert
              severity={
                errorCount > 0
                  ? 'warning'
                  : notification.notification_type === 'TASK_FAILED'
                  ? 'error'
                  : 'success'
              }
              variant="outlined"
              sx={{ borderRadius: 1.5 }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {notification.message}
              </Typography>
            </Alert>
          )}

          {/* Background task execution summary */}
          {task && (
            <Card
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 1.5,
                bgcolor: 'background.neutral',
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">
                  Task Execution Details
                </Typography>
                <Label
                  variant="filled"
                  color={
                    task.status === 'COMPLETED'
                      ? 'success'
                      : task.status === 'FAILED'
                      ? 'error'
                      : 'info'
                  }
                >
                  {task.status} ({task.progress_percent}%)
                </Label>
              </Stack>

              <LinearProgress
                variant="determinate"
                value={task.progress_percent || 0}
                color={task.status === 'FAILED' ? 'error' : 'primary'}
                sx={{ height: 7, borderRadius: 1, mb: 1.5 }}
              />

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                spacing={1}
                sx={{ typography: 'caption', color: 'text.secondary' }}
              >
                <span>
                  <strong>Task ID:</strong> {task.task_id}
                </span>
                {task.progress_total > 0 && (
                  <span>
                    <strong>Processed:</strong> {task.progress_current} / {task.progress_total}
                  </span>
                )}
                {task.status_message && (
                  <span>
                    <strong>Status:</strong> {task.status_message}
                  </span>
                )}
              </Stack>
            </Card>
          )}

          {/* Quick Metrics Cards */}
          {(createdCount !== null || errorCount > 0 || schoolName) && (
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: 'repeat(1, 1fr)',
                  sm: errorCount > 0 ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
                },
              }}
            >
              {createdCount !== null && (
                <Card
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    bgcolor: (theme) => theme.palette.mode === 'light' ? '#f0fdf4' : 'background.paper',
                    borderColor: 'success.light',
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'success.lighter',
                        color: 'success.main',
                      }}
                    >
                      <Iconify icon="eva:checkmark-fill" width={22} />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ color: 'success.darker', fontWeight: 700 }}>
                        {createdCount}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Successfully Imported
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              )}

              {errorCount > 0 && (
                <Card
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    bgcolor: (theme) => theme.palette.mode === 'light' ? '#fef2f2' : 'background.paper',
                    borderColor: 'error.light',
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'error.lighter',
                        color: 'error.main',
                      }}
                    >
                      <Iconify icon="eva:alert-triangle-fill" width={22} />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ color: 'error.darker', fontWeight: 700 }}>
                        {errorCount}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Error / Unprocessed
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              )}

              {schoolName && (
                <Card
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    bgcolor: (theme) => theme.palette.mode === 'light' ? '#f8fafc' : 'background.paper',
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'primary.lighter',
                        color: 'primary.main',
                      }}
                    >
                      <Iconify icon="solar:buildings-2-bold" width={22} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
                        {schoolName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                        {centerName ? `Center: ${centerName}` : 'Registered School'}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              )}
            </Box>
          )}

          {/* Detailed Error Records Section */}
          {normalizedErrors.length > 0 ? (
            <Box sx={{ mt: 1 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1.5}
                sx={{ mb: 1.5 }}
              >
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Unprocessed Error Records
                    </Typography>
                    <Label color="error" variant="soft">
                      {normalizedErrors.length} records
                    </Label>
                  </Stack>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Inspect which student records were not imported and the exact reason for failure.
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<Iconify icon="solar:file-download-bold" />}
                    onClick={handleExportErrorsXlsx}
                  >
                    Export Errors (.xlsx)
                  </Button>
                </Stack>
              </Stack>

              {/* Search filter for errors */}
              <TextField
                size="small"
                fullWidth
                placeholder="Search error records by student name, phone, class, or reason..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="solar:magnifer-bold" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <Iconify icon="mingcute:close-line" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{ mb: 1.5 }}
              />

              {/* Error Records Table */}
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                <Scrollbar sx={{ maxHeight: 380 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 45, fontWeight: 700 }}>#</TableCell>
                        <TableCell sx={{ minWidth: 150, fontWeight: 700 }}>Student Name</TableCell>
                        <TableCell sx={{ minWidth: 90, fontWeight: 700 }}>Class</TableCell>
                        <TableCell sx={{ minWidth: 110, fontWeight: 700 }}>Phone</TableCell>
                        <TableCell sx={{ minWidth: 130, fontWeight: 700 }}>Parent</TableCell>
                        <TableCell sx={{ minWidth: 260, fontWeight: 700 }}>Failure Reason</TableCell>
                        <TableCell sx={{ width: 40 }} />
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredErrors
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((err) => {
                          const isExpanded = expandedRow === err.id;
                          return (
                            <>
                              <TableRow
                                key={err.id}
                                hover
                                sx={{
                                  '&:last-child td, &:last-child th': { border: 0 },
                                  bgcolor: isExpanded ? 'action.hover' : 'inherit',
                                }}
                              >
                                <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                  {err.id}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {err.name}
                                  </Typography>
                                  {err.gender && err.gender !== '-' && (
                                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                                      {err.gender}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Label variant="soft" color="default" sx={{ fontSize: 11 }}>
                                    {err.className}
                                  </Label>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                    {err.phone}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {err.parentName}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Alert
                                    severity="error"
                                    variant="soft"
                                    icon={<Iconify icon="solar:danger-triangle-bold" />}
                                    sx={{
                                      py: 0.5,
                                      px: 1,
                                      fontSize: 12,
                                      lineHeight: 1.4,
                                      '& .MuiAlert-message': { py: 0 },
                                    }}
                                  >
                                    {err.error}
                                  </Alert>
                                </TableCell>
                                <TableCell>
                                  <Tooltip title={isExpanded ? 'Collapse' : 'View row details'}>
                                    <IconButton
                                      size="small"
                                      onClick={() => setExpandedRow(isExpanded ? null : err.id)}
                                    >
                                      <Iconify
                                        icon={
                                          isExpanded
                                            ? 'eva:arrow-ios-upward-fill'
                                            : 'eva:arrow-ios-downward-fill'
                                        }
                                      />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>

                              {isExpanded && (
                                <TableRow sx={{ bgcolor: 'background.neutral' }}>
                                  <TableCell colSpan={7} sx={{ py: 1.5, px: 3 }}>
                                    <Box
                                      sx={{
                                        display: 'grid',
                                        gap: 1.5,
                                        gridTemplateColumns: {
                                          xs: 'repeat(1, 1fr)',
                                          sm: 'repeat(3, 1fr)',
                                        },
                                        typography: 'caption',
                                      }}
                                    >
                                      <div>
                                        <strong>Date of Birth:</strong> {err.dob}
                                      </div>
                                      <div>
                                        <strong>Desired Stream:</strong> {err.desiredStream}
                                      </div>
                                      <div>
                                        <strong>City:</strong> {err.city}
                                      </div>
                                      <div>
                                        <strong>Address:</strong> {err.address}
                                      </div>
                                      <div>
                                        <strong>Pincode:</strong> {err.pincode}
                                      </div>
                                      <div>
                                        <strong>State:</strong> {err.state || '-'}
                                      </div>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              )}
                            </>
                          );
                        })}

                      {filteredErrors.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ py: 4, textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              No matching error records found for "{searchQuery}".
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Scrollbar>

                <TablePagination
                  component="div"
                  count={filteredErrors.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </TableContainer>
            </Box>
          ) : (
            normalizedErrors.length === 0 && (
              <Box
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: 1.5,
                  bgcolor: 'background.neutral',
                }}
              >
                <Iconify
                  icon="solar:check-circle-bold-duotone"
                  width={48}
                  sx={{ color: 'success.main', mb: 1 }}
                />
                <Typography variant="subtitle2">
                  No Error Records
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  All records for this operation were processed successfully.
                </Typography>
              </Box>
            )
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
        {normalizedErrors.length > 0 && (
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<Iconify icon="solar:file-download-bold" />}
            onClick={handleExportErrorsXlsx}
          >
            Download Error Report
          </Button>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {notification.action_url && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<Iconify icon="solar:arrow-right-up-bold" />}
            onClick={() => {
              onClose();
              if (onNavigate) {
                onNavigate(notification.action_url);
              }
            }}
          >
            Go to Exam Profile
          </Button>
        )}

        <Button variant="outlined" color="inherit" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

NotificationDetailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  notification: PropTypes.object,
  onNavigate: PropTypes.func,
};
